// One Notebook cell's place in the list (#2026). A chat can hold hundreds of
// tables, charts and scripts; drawing them all when the Notebook opens froze
// the browser. A slot draws its cell only once it comes within a screen or so
// of the view, and keeps it drawn after, so scrolling back costs nothing and a
// cell's own state (an open editor, a fetched script) survives.
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'

/** How far outside the view a cell starts to draw. */
const REACH = '1200px 0px'
/** The height a cell stands at before it draws, so the scrollbar is about right. */
const PLACEHOLDER_HEIGHT = 320

/**
 * The element the cells scroll in (the dialog's content). It must be the
 * observer's root: with the viewport as root, the scrolling content clips every
 * cell below its fold before REACH applies, so only on-screen cells drew.
 */
export const NotebookScrollRoot = createContext<React.RefObject<HTMLElement | null> | null>(null)

export interface NotebookSlotProps {
  cellId: string
  /** Draw now, wherever it is (the cell "Open in notebook" named). */
  eager?: boolean
  children: React.ReactNode
}

export const NotebookSlot: React.FC<NotebookSlotProps> = ({ cellId, eager, children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const scrollRoot = useContext(NotebookScrollRoot)
  // Without IntersectionObserver there is no way to tell what is near: draw it.
  const [shown, setShown] = useState(() => !!eager || typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    if (eager) setShown(true)
  }, [eager])

  // Watch until the cell first comes near the view.
  useEffect(() => {
    if (shown || !ref.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setShown(true)
        observer.disconnect()
      }
    }, { root: scrollRoot?.current ?? null, rootMargin: REACH })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [shown, scrollRoot])

  return (
    <Box ref={ref} data-notebook-slot={cellId} sx={shown ? undefined : { minHeight: PLACEHOLDER_HEIGHT, mb: 1.5 }}>
      {shown ? children : null}
    </Box>
  )
}

export default NotebookSlot
